import axios, { AxiosRequestConfig, isAxiosError } from 'axios';
import axiosRetry from 'axios-retry';

import Cookies from 'js-cookie';
import { getIsSlateSkinFromDomain } from '|/context';
import accessToken, { getCvAccessToken } from '|/helpers/access-token';
import { getRequestHeaders } from './request-headers';

declare global {
  interface Window {
    CarvanaEvents: { emitter: { emit: (_: string) => void } };
  }
}

function uuid(seed: string | null = null): string {
  const seedInt = seed === null ? null : parseInt(seed);
  return seedInt !== null
    ? (seedInt ^ ((Math.random() * 16) >> (seedInt / 4))).toString(16)
    : `10000000${-1e3}${-4e3}${-8e3}${-1e11}`.replace(/[018]/g, uuid);
}

const getBaseUrl = () => {
  const slateDomain = process.env.SLATE_DOMAIN;
  const isSlateOrigin =
    !!slateDomain && window.location?.hostname?.toLowerCase() === slateDomain.toLowerCase();
  return isSlateOrigin ? window.location.origin : process.env.BASE_URL;
};

const getTokenRefreshUrl = () => `${getBaseUrl()}${process.env.CVNA_APP_AUTH_TOKEN}`;

const axiosInstance = axios.create({
  baseURL: getBaseUrl(),
  maxRedirects: 0,
  headers: getRequestHeaders()
});

axiosRetry(axiosInstance, {
  retries: 0,
  retryDelay: retryCount => retryCount * 1000
});

const fetchAuthToken = () =>
  axiosInstance
    .post(getTokenRefreshUrl(), {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    })
    .then(res => {
      return res.data;
    })
    .catch(async error => {
      const { response: { status = null } = {} } = error;

      const isStatusFourHundred = status >= 400;

      if (isStatusFourHundred) {
        await axiosInstance.post('/user-logout').catch(() => {
          for (const token of ['CVAccessToken', 'CVIdToken']) {
            Cookies.remove(token);
          }
        });
        if (getIsSlateSkinFromDomain() && status >= 500) {
          window.location.href = `${getBaseUrl()}/speedbump`;
        } else if (getIsSlateSkinFromDomain()) {
          window.location.href = `${getBaseUrl()}/login?provider=SlateFusionAuth`;
        } else {
          window.location.href = `${getBaseUrl()}/purchase`;
        }
      }
    });

const fetchAndSaveToken = async () => {
  await fetchAuthToken();
  return getCvAccessToken();
};

export const refreshTokenIfMissing = async () => {
  const { cvIdToken, cvAccessToken, isValidToken } = accessToken();

  if (cvIdToken && !cvAccessToken && !isValidToken) {
    await fetchAndSaveToken();
    return;
  }
  return;
};

axiosInstance.interceptors.request.use(
  async config => {
    const originalConfig = { headers: {}, ...config };
    originalConfig.headers['correlation-id'] = uuid();
    const token = accessToken();
    const cvAccessToken = token.cvAccessToken;

    if (cvAccessToken) originalConfig.headers.Authorization = `Bearer ${cvAccessToken}`;

    if (originalConfig.method === 'get' && /MSIE \d|Trident.*rv:/.test(navigator.userAgent)) {
      // add a random query param to remove caching on ie11
      originalConfig.url += `?${new Date().getTime()}`;
    }

    return originalConfig;
  },
  error => {
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  async response => response,
  async error => {
    if (!isAxiosError(error)) {
      return Promise.reject(error);
    }
    const { response: { status = null } = {} } = error;

    const originalRequest = error.config as AxiosRequestConfig & {
      isRetry?: boolean;
    };
    if (!originalRequest || originalRequest.url === getTokenRefreshUrl()) {
      return Promise.reject(error);
    }

    if (status === 401) {
      if (originalRequest.isRetry) {
        throw error;
      } else {
        originalRequest.isRetry = true;
        const newToken = await fetchAndSaveToken();

        if (newToken) {
          if (originalRequest.headers === undefined) {
            originalRequest.headers = {};
          }
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return axiosInstance(originalRequest);
        }
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
